<template>
	<div
		v-if="orderNoteEnabled"
		class=""
	>
		<div class="accordion-item border-b">
			<h3>
				<button
					:aria-expanded="showOrderNote ? 'true' : 'false'"
					aria-controls="order-note-panel"
					id="order-note-header"
					class="flex items-center justify-between w-full py-4 cursor-pointer"
					@click="handleOrderNoteToggle"
					@keydown="handleKeyDown"
				>
			<div class="flex items-center gap-2 justify-between w-[calc(100%-1rem)] text-left flex-wrap">
				<span class="flex gap-2 relative items-center">
					<span
						v-if="showAccordionIcons"
						class="mini-cart__util-icon"
					>
						<MiniCartIconNote />
					</span>
					{{ orderNoteHeading }}
				</span>
						<span
							v-if="orderNote && !showOrderNote && !hasUnsavedChanges"
							class="mini-cart__truncated-preview-text"
						>{{ orderNote }}</span>
					</div>
					<svg
						:class="showOrderNote ? 'rotate-180' : 'rotate-0'"
						class="w-4 h-4 transition-transform duration-200"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M19 9l-7 7-7-7"
						></path>
					</svg>
				</button>
			</h3>
			<button
        v-if="showOrderNote"
        @click="handleOrderNoteToggle"
        class="w-full h-full fixed inset-0 mini-cart__blocker"
        style="
          background-color: var(--global-mask-color);
          backdrop-filter: var(--global-mask-blur);
        "
      >
        <span class="sr-only">close</span>
      </button>
			<div
				id="order-note-panel"
				aria-labelledby="order-note-header"
				class="overflow-hidden transition-all duration-300 ease-in-out mini-cart__slide-up-panel"
				:class="showOrderNote ? 'max-h-96 opacity-100 panel_active' : 'panel_disabled'"
				@keydown="handlePanelKeyDown"
			>
				<div class="flex justify-between items-center mb-2">
					<span class="">	{{ orderNoteHeading }}</span>
					<button
						class="w-12 h-12 right-0 flex items-center justify-center"
						@click.passive="toggleOrderNote"
					>
						<MiniCartIconClose />
						<span class="sr-only">Close</span>
					</button>
				</div>
				<form
					class="text-area-wrap relative transition-all duration-300 ease-in-out pb-4"
					:class="showOrderNote ? 'opacity-100' : 'opacity-0'"
					id="note-wrap"
					@submit.prevent="handleSaveNote($event)"
				>
					<fieldset>
						<div class="relative">
							<textarea
								v-model="orderNote"
								:disabled="isOrderNoteUpdating"
								@keydown="checkCharacters($event)"
								class="block w-full border p-2 resize-none overflow-auto"
								name="note"
								id="cart-note"
								maxlength="280"
								rows="4"
								:placeholder="orderNotePlaceholder"
							></textarea>
							<div
								id="count1"
								class="absolute bottom-0 right-0 text-xs p-2"
							>
								<span id="current_count">{{ orderNote.length }}</span>
								<span id="maximum_count">/280</span>
							</div>
						</div>

						<div class="flex justify-end items-start gap-4 mt-4">
							<span
								class="flex-1 text-xs"
								id="special-character-error"
								ref="specialCharacterError"
							>{{ orderNoteErrorText }}</span>

							<!-- Show Save and Clear buttons when there's content -->
							<template v-if="orderNote.length">
								<button
									type="button"
									class="mini-cart__button"
									@click="clearOrderNote"
								>{{ orderNoteClearButton }}</button>
								<button
									type="submit"
									class="mini-cart__button"
									:class="hasUnsavedChanges ? 'button' : 'button'"
									ref="saveCartNote"
								>{{ hasUnsavedChanges ? orderNoteSaveUnsavedButton : orderNoteSaveButton }}</button>
							</template>

							<!-- Show Save button when there's no content -->
							<button
								v-else
								type="submit"
								class="mini-cart__button"
								:class="hasUnsavedChanges ? 'button' : 'button'"
								ref="saveCartNote"
							>{{ hasUnsavedChanges ? orderNoteSaveUnsavedButton : orderNoteSaveButton }}</button>
						</div>
					</fieldset>
				</form>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useAccordion } from './composables/useAccordion';
import MiniCartIconClose from './mini-cart--icon-close.vue';
import MiniCartIconNote from './mini-cart--icon-note.vue';
// Props
const props = defineProps({
	cartData: {
		type: Object,
		default: () => ({})
	}
});

// Emits
const emit = defineEmits(['update-cart', 'cart-updated']);

// Order Note Vars
const orderNoteEnabled = computed(() => window.miniCartCustomSettings.orderNoteEnabled);
const orderNoteHeading = computed(() => window.miniCartCustomSettings.orderNoteHeading || 'Add a handwritten note');
const orderNotePlaceholder = computed(() => window.miniCartCustomSettings.orderNotePlaceholder || 'Add a note to your order (optional)');
const orderNoteErrorText = computed(() => window.miniCartCustomSettings.orderNoteErrorText || '*Special characters not allowed.');
const orderNoteClearButton = computed(() => window.miniCartCustomSettings.orderNoteClearButton || 'Clear');
const orderNoteSaveButton = computed(() => window.miniCartCustomSettings.orderNoteSaveButton || 'Save');
const orderNoteSaveUnsavedButton = computed(() => window.miniCartCustomSettings.orderNoteSaveUnsavedButton || 'Save*');
const orderNote = ref('');
// const showOrderNote = ref(false);
const { isOpen: showOrderNote, toggle: toggleOrderNote, close: closeOrderNote } = useAccordion('order-note');
const showAccordionIcons = ref(window.miniCartCustomSettings?.showAccordionIcons ?? true);
const isOrderNoteUpdating = ref(false);
const hasUnsavedChanges = ref(false); // Track if there are unsaved changes

// Refs
const specialCharacterError = ref(null);
const saveCartNote = ref(null);

// Manual save only - no debouncing needed

// Handle save note (form submission or save button)
function handleSaveNote(e) {
	updateCart(e, orderNote.value);
}


function updateCart(e, note, closeOnSuccess = true) {
	isOrderNoteUpdating.value = true;
	fetch('/cart/update.js', {
		method: 'POST',
		credentials: 'same-origin',
		headers: {
			'Content-Type': 'application/json',
			'X-Requested-With': 'XMLHttpRequest'
		},
		body: JSON.stringify({ note: note }),
	})
		.then(response => response.json())
		.then(data => {
			if (specialCharacterError.value) specialCharacterError.value.classList.add('hidden');
			isOrderNoteUpdating.value = false;
			hasUnsavedChanges.value = false;
			orderNote.value = data.note;
			if (closeOnSuccess) closeOrderNote();
			emit('cart-updated', data);
		})
		.catch((error) => {
			console.error('Error:', error);
			isOrderNoteUpdating.value = false;
		});
}


// Clear order note
function clearOrderNote(e) {
	orderNote.value = '';
	updateCart(e, '', false);
}

// Manual save only - no auto-save to prevent focus loss

//Check length of characters in Note field.
function checkCharacters(evt) {
	console.log('checkCharacters', evt)
	const allowedCharacters = /^[A-Za-z0-9~@#$^*()_+\[\]{}|\\,.?!: -]+$/;
	if (!allowedCharacters.test(evt.key)) {
		evt.preventDefault();
		if (specialCharacterError.value) {
			specialCharacterError.value.classList.remove('hidden');
		}
	} else {
		if (specialCharacterError.value) {
			specialCharacterError.value.classList.add('hidden');
		}
	}
}

const handleOrderNoteToggle = () => {
  toggleOrderNote();
};

// Handle keyboard events for accordion header
function handleKeyDown(event) {
	if (event.key === 'Enter' || event.key === ' ') {
		event.preventDefault();
		handleOrderNoteToggle();
	}
}

// Handle Escape key in accordion panel
function handlePanelKeyDown(event) {
	if (event.key === 'Escape') {
		closeOrderNote();
		// Return focus to header button
		const headerButton = document.getElementById('order-note-header');
		if (headerButton) {
			headerButton.focus();
		}
	}
}

async function removeOrderNote() {
	try {
		isOrderNoteUpdating.value = true;
		const response = await fetch('/cart/update.js', {
			method: 'POST',
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/json',
				'X-Requested-With': 'XMLHttpRequest'
			},
			body: JSON.stringify({ note: '' }),
		});
		const data = await response.json();
		orderNote.value = '';
		isOrderNoteUpdating.value = false;
		emit('cart-updated', data);
	} catch (error) {
		console.error('Error:', error);
		isOrderNoteUpdating.value = false;
	}
}

// Watch for changes and mark as unsaved
watch(() => orderNote.value, (newNote, oldNote) => {
	if (newNote !== oldNote) {
		hasUnsavedChanges.value = true;
	}
});

// Watch for cart data changes from parent
watch(() => props.cartData, (newCartData) => {
	if (newCartData && newCartData.note !== undefined) {
		orderNote.value = newCartData.note;
		hasUnsavedChanges.value = false;
	}
}, { immediate: true });

// Expose methods that parent might need
defineExpose({
	updateCart,
	orderNote
});
</script>
